/**
 * zg-strings-lp.js — head fix-ups for the Easy-Play Strings landing page.
 *
 * Loaded only by `sections/zg-strings-header.liquid`, which only ever renders
 * on `templates/page.zg-strings-lp.json`. It does two small jobs that Liquid
 * cannot do from inside a JSON template, and nothing else — the page's
 * behaviour (add to cart, cart drawer, the video facade) is Horizon's and the
 * theme's existing components, not this file.
 *
 * 1. `<meta name="robots">` into `<head>`.
 *
 *    Spec §13 keeps live's `noindex, nofollow` and says to emit it "from the
 *    template — not from theme.liquid, not from a global setting". A JSON
 *    template can only emit markup through its sections, and sections render
 *    inside `<body>`, where a robots tag is not reliably honoured. So the
 *    section emits the tag and this moves the very same element into `<head>`.
 *    Google documents that a robots meta tag added by JavaScript is respected,
 *    and the tag only ever tightens (index -> noindex), never loosens.
 *
 *    `layout/theme.liquid` and `snippets/meta-tags.liquid` are owned by another
 *    workstream and are deliberately not touched; if a per-template head hook
 *    is ever added there, delete this half of the file and emit the tag from it.
 *
 * 2. Opening the cart drawer after an add.
 *
 *    Spec §8 step 2: each of the three CTAs must open the theme's existing cart
 *    drawer to confirm the add — no redirect, so the drawer IS the confirmation.
 *    Horizon already does this, but only when the theme setting
 *    `auto_open_cart_drawer` is on; it defaults to false and this store has
 *    never turned it on (`config/settings_data.json` does not carry the key).
 *
 *    Turning it on would change every add-to-cart in the store — product page,
 *    quick add, the gift auto-add — which is not this page's call and not this
 *    workstream's file. So the drawer is opened from here instead, on the one
 *    template this script loads on. If the store later switches the setting on,
 *    Horizon's own handler runs first and `isOpen` makes this a no-op.
 *
 * 3. The page title.
 *
 *    Spec §1 fixes the title as live's, verbatim. `snippets/meta-tags.liquid`
 *    builds `<title>` from `page_title` and appends the shop name, so the exact
 *    string cannot come out of the theme. The definitive fix is the page
 *    record's SEO title in the admin; until that is set, a section setting
 *    carries the string and this applies it. Leave the setting blank and
 *    nothing happens — whatever the page record says is kept.
 */

const ROOT_ID = 'zg-strings-lp-head';

/** Moves the section-emitted robots tag into <head>, once. */
function hoistRobotsMeta() {
  const emitted = document.querySelector(`meta[name="robots"][data-${ROOT_ID}]`);

  if (!emitted || emitted.parentElement === document.head) return;

  // A robots tag already in <head> wins: never loosen a directive the store
  // (or an app) set deliberately, only move ours up when nothing competes.
  const existing = document.head.querySelector('meta[name="robots"]');

  if (existing) {
    emitted.remove();
    return;
  }

  document.head.appendChild(emitted);
}

/** Applies the verbatim page title carried on the section, when one is set. */
function applyTitleOverride() {
  const source = document.querySelector(`[data-${ROOT_ID}-title]`);
  const title = source?.getAttribute(`data-${ROOT_ID}-title`)?.trim();

  if (!title || document.title === title) return;

  document.title = title;
}

/**
 * Opens the cart drawer once an add from this page has actually landed.
 *
 * The event carries a promise that Horizon's own listeners await; waiting on it
 * means the drawer opens over refreshed contents, and a failed add (`didError`)
 * leaves it shut so the error on the button is what the customer sees.
 */
function openCartDrawerOnAdd() {
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add') return;

    const drawer = document.getElementById('cart-drawer');
    if (typeof drawer?.open !== 'function') return;

    const show = (result) => {
      if (result?.detail?.didError || drawer.isOpen) return;
      drawer.open();
    };

    if (event.promise?.then) event.promise.then(show).catch(() => {});
    else show();
  });
}

hoistRobotsMeta();
applyTitleOverride();
openCartDrawerOnAdd();
